import type { MuxPlayerRefAttributes } from "@mux/mux-player-react";

type MuxPlayerElement = MuxPlayerRefAttributes;

const HIDDEN_CONTROL_SELECTORS = [
  "media-seek-backward-button",
  "media-seek-forward-button",
  "media-rendition-menu-button",
  "media-rendition-menu",
  "media-audio-track-menu-button",
  "media-audio-track-menu",
  "media-captions-menu-button",
  "media-captions-menu",
  "media-airplay-button",
  "media-cast-button",
  ".spacer",
] as const;

const CONTROL_BAR_SELECTOR = 'media-control-bar[part="control-bar bottom"]';
const TIME_RANGE_SELECTOR = 'media-time-range[part="bottom time range"]';
const CURRENT_TIME_SELECTOR = 'media-time-display[part="bottom time display"]';
const TOP_BAR_SELECTOR = 'media-control-bar[part="control-bar top"]';

const GROUP_LEFT = "left";
const GROUP_RIGHT = "right";
const MOBILE_MAX_WIDTH = 767;
const WAYFLYER_CONTROL_STYLE_ID = "wayflyer-control-styles";
const POPOVER_HIDE_DELAY_MS = 250;

const WAYFLYER_CONTROL_STYLES = `
  media-controller:not([data-wayflyer-layout]) :is(
    media-control-bar[part="control-bar bottom"],
    media-time-range[part="bottom time range"]
  ) {
    display: none !important;
    pointer-events: none !important;
  }

  media-controller[data-wayflyer-layout] media-time-range[part="bottom time range"] {
    display: flex !important;
  }

  media-controller[data-wayflyer-layout]:not([data-wayflyer-controls-ready]) :is(
    media-control-bar[part="control-bar bottom"],
    media-time-range[part="bottom time range"]
  ) {
    visibility: hidden !important;
    pointer-events: none !important;
  }

  media-controller[data-wayflyer-controls-ready] :is(
    media-control-bar[part="control-bar bottom"],
    media-time-range[part="bottom time range"]
  ) {
    visibility: visible !important;
    pointer-events: auto !important;
  }

  media-controller[data-wayflyer-layout="desktop"] media-control-bar[part="control-bar bottom"] {
    box-sizing: border-box;
    gap: 16px;
    padding: 0 58px 46px;
  }

  media-controller[data-wayflyer-layout="mobile"] {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  media-controller[data-wayflyer-layout="mobile"] media-control-bar[part="control-bar bottom"] {
    box-sizing: border-box;
    order: 1;
    gap: 0;
    justify-content: space-between;
    padding: 0 20px 0;
  }

  media-controller[data-wayflyer-layout="mobile"] media-time-range[data-wayflyer-inline] {
    order: 2;
    position: static !important;
    width: 100% !important;
    flex: none !important;
    box-sizing: border-box;
    padding: 8px 20px 15px !important;
    margin: 0 !important;
  }

  media-time-range[data-wayflyer-inline] {
    --media-range-bar-color: #fff;
    --media-range-thumb-background: #fff;
    --media-range-track-background: rgb(255 255 255 / 0.1);
  }

  media-time-range[data-wayflyer-inline]::part(thumb) {
    background: #fff !important;
    box-shadow: none;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-time-separator] {
    color: inherit;
    font-size: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    white-space: pre;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-time-cluster] {
    display: inline-flex;
    align-items: center;
    gap: 0;
    min-width: 0;
    flex-shrink: 1;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-time-cluster] media-time-display,
  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-time-cluster] [data-wayflyer-time-separator] {
    min-height: auto !important;
    height: auto !important;
    padding: 0 !important;
    margin: 0;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-time-cluster] media-time-display:is(
    :hover,
    :focus-visible
  ) {
    min-height: 32px !important;
    height: 32px !important;
    padding: 0 8px !important;
    border-radius: 200px;
    background: rgb(255 255 255 / 0.2);
  }

  media-controller[data-wayflyer-layout="mobile"] media-play-button[part="bottom play button"] {
    display: inline-flex !important;
  }

  media-controller[data-wayflyer-layout="mobile"] media-play-button[part="center play button"] {
    display: none !important;
  }

  media-controller[data-wayflyer-layout="mobile"] media-pip-button {
    display: inline-flex !important;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-group="left"] {
    min-width: 0;
    flex: 1 1 auto;
    overflow: visible;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-group="right"] {
    flex-shrink: 0;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-group="left"] media-time-display,
  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-group="left"] [data-wayflyer-time-separator] {
    min-height: auto !important;
    height: auto !important;
    padding: 0 !important;
  }

  [data-wayflyer-volume-control],
  [data-wayflyer-playback-rate-control] {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 32px;
    width: auto;
    height: 32px;
  }

  [data-wayflyer-volume-control]::before,
  [data-wayflyer-playback-rate-control]::before {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 100%;
    transform: translateX(-50%);
    pointer-events: auto;
    z-index: 1;
  }

  [data-wayflyer-volume-control]::before {
    width: 22px;
    height: calc(8px + 95px);
  }

  [data-wayflyer-playback-rate-control]::before {
    width: 50px;
    height: calc(8px + 180px);
  }

  [data-wayflyer-volume-popup] {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 22px;
    height: 95px;
    padding: 8px;
    background: rgb(255 255 255 / 0.2);
    border: 1px solid rgb(255 255 255 / 0.2);
    border-radius: 12px;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 150ms cubic-bezier(0.23, 1, 0.32, 1),
      visibility 150ms cubic-bezier(0.23, 1, 0.32, 1);
    z-index: 2;
  }

  [data-wayflyer-volume-control]:hover [data-wayflyer-volume-popup],
  [data-wayflyer-volume-control]:focus-within [data-wayflyer-volume-popup],
  [data-wayflyer-volume-control][data-wayflyer-popover-open="true"] [data-wayflyer-volume-popup] {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  [data-wayflyer-volume-popup] media-volume-range {
    display: block !important;
    width: 77px;
    height: 4px;
    transform: rotate(-90deg);
    transform-origin: center center;
    --media-range-track-height: 4px;
    --media-range-track-background: rgb(255 255 255 / 0.1);
    --media-range-track-border-radius: 200px;
    --media-range-bar-color: #fff;
    --media-range-thumb-width: 0;
    --media-range-thumb-height: 0;
    --media-range-thumb-transform: scale(0);
  }

  [data-wayflyer-volume-popup] media-volume-range:hover {
    --media-range-thumb-width: 12px;
    --media-range-thumb-height: 12px;
    --media-range-thumb-transform: scale(1);
  }

  [data-wayflyer-playback-rate-control] media-playback-rate-selectmenu {
    position: relative;
    display: inline-flex;
    flex-shrink: 0;
  }

  [data-wayflyer-playback-rate-control] media-playback-rate-menu-button,
  [data-wayflyer-playback-rate-control] media-playback-rate-button {
    --media-control-padding: 0 8px;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    width: auto !important;
    max-width: none;
    white-space: nowrap;
  }

  [data-wayflyer-playback-rate-control] media-playback-rate-menu,
  [data-wayflyer-playback-rate-control] media-playback-rate-listbox {
    position: absolute !important;
    bottom: calc(100% + 8px) !important;
    left: 50% !important;
    right: auto !important;
    top: auto !important;
    transform: translateX(-50%) !important;
    --media-menu-flex-direction: column;
    --media-menu-background: rgb(255 255 255 / 0.2);
    --media-menu-border: 1px solid rgb(255 255 255 / 0.2);
    --media-menu-border-radius: 200px;
    --media-menu-gap: 0;
    --media-menu-item-max-width: none;
    --media-menu-overflow: visible;
    --media-menu-item-hover-background: rgb(255 255 255 / 0.2);
    --media-menu-item-checked-background: var(--pulse-700);
    --media-menu-item-hover-outline: none;
    --media-listbox-background: rgb(255 255 255 / 0.2);
    --media-listbox-border-radius: 200px;
    --media-listbox-flex-direction: column;
    --media-listbox-gap: 0;
    --media-option-select-indicator-display: none;
    --media-option-hover-background: rgb(255 255 255 / 0.2);
    --media-option-selected-background: var(--pulse-700);
    --media-option-focus-shadow: none;
    --media-menu-item-focus-shadow: none;
    background: rgb(255 255 255 / 0.2) !important;
    border: 1px solid rgb(255 255 255 / 0.2) !important;
    backdrop-filter: none;
    box-sizing: border-box;
    width: max-content !important;
    min-width: 0;
    padding: 8px;
    overflow: visible !important;
    z-index: 2;
  }

  [data-wayflyer-playback-rate-control] media-playback-rate-menu {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition:
      opacity 150ms cubic-bezier(0.23, 1, 0.32, 1),
      visibility 150ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  [data-wayflyer-playback-rate-control]:hover media-playback-rate-menu:not([hidden]),
  [data-wayflyer-playback-rate-control]:focus-within media-playback-rate-menu:not([hidden]),
  [data-wayflyer-playback-rate-control][data-wayflyer-popover-open="true"]
    media-playback-rate-menu:not([hidden]),
  [data-wayflyer-playback-rate-control][data-wayflyer-rate-open="true"]
    media-playback-rate-listbox {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  [data-wayflyer-playback-rate-control] media-playback-rate-menu media-chrome-menu-item {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: center;
    width: 32px;
    min-width: 32px;
    max-width: 32px;
    min-height: 32px;
    height: 32px;
    padding: 0;
    white-space: nowrap;
    border-radius: 200px;
    border: 1px solid transparent;
    font-size: 12px;
    letter-spacing: -0.24px;
    line-height: 1.4;
  }

  media-controller[data-wayflyer-layout="mobile"] [data-wayflyer-playback-rate-control]::before {
    left: auto;
    right: 0;
    transform: none;
    width: 36px;
    height: calc(4px + 152px);
  }

  media-controller[data-wayflyer-layout="mobile"]
    [data-wayflyer-playback-rate-control]
    media-playback-rate-menu,
  media-controller[data-wayflyer-layout="mobile"]
    [data-wayflyer-playback-rate-control]
    media-playback-rate-listbox {
    bottom: calc(100% + 4px) !important;
    left: auto !important;
    right: 0 !important;
    transform: none !important;
    padding: 4px;
    overflow: visible !important;
  }

  media-controller[data-wayflyer-layout="mobile"]
    [data-wayflyer-playback-rate-control]
    media-playback-rate-menu
    media-chrome-menu-item {
    width: 24px;
    min-width: 24px;
    max-width: 24px;
    min-height: 24px;
    height: 24px;
    font-size: 10px;
    letter-spacing: -0.2px;
    border: 1px solid transparent;
    border-radius: 200px;
  }

  media-controller[data-wayflyer-layout="mobile"]
    [data-wayflyer-playback-rate-control]
    media-playback-rate-menu
    media-chrome-menu-item:hover,
  media-controller[data-wayflyer-layout="mobile"]
    [data-wayflyer-playback-rate-control]
    media-playback-rate-menu
    media-chrome-menu-item:focus-visible {
    background: rgb(255 255 255 / 0.2);
    border-color: rgb(255 255 255 / 0.2);
    box-shadow: none;
    outline: none;
  }

  media-controller[data-wayflyer-layout="mobile"]
    [data-wayflyer-playback-rate-control]
    media-playback-rate-menu
    media-chrome-menu-item[aria-checked="true"] {
    background: var(--pulse-700);
    border-color: rgb(255 255 255 / 0.2);
  }
`;

function getThemeShadowRoot(player: MuxPlayerElement) {
  return player.shadowRoot?.querySelector("media-theme")?.shadowRoot ?? null;
}

function ensureWayflyerControlStyles(themeShadowRoot: ShadowRoot) {
  themeShadowRoot.getElementById("wayflyer-volume-control-styles")?.remove();

  const existingStyle = themeShadowRoot.getElementById(
    WAYFLYER_CONTROL_STYLE_ID
  );
  if (existingStyle) {
    existingStyle.textContent = WAYFLYER_CONTROL_STYLES;
    return;
  }

  const style = document.createElement("style");
  style.id = WAYFLYER_CONTROL_STYLE_ID;
  style.textContent = WAYFLYER_CONTROL_STYLES;
  themeShadowRoot.appendChild(style);
}

function isMobileLayout(player: MuxPlayerElement) {
  return player.clientWidth <= MOBILE_MAX_WIDTH;
}

function ensureTimeSeparator(container: Element) {
  let separator = container.querySelector(
    "[data-wayflyer-time-separator]"
  ) as HTMLElement | null;

  if (!separator) {
    separator = document.createElement("span");
    separator.dataset["wayflyerTimeSeparator"] = "";
    separator.setAttribute("aria-hidden", "true");
    separator.textContent = " / ";
  }

  return separator;
}

function ensureTimeCluster(leftGroup: Element) {
  let cluster = leftGroup.querySelector(
    "[data-wayflyer-time-cluster]"
  ) as HTMLElement | null;

  if (!cluster) {
    cluster = document.createElement("div");
    cluster.dataset["wayflyerTimeCluster"] = "";
    cluster.style.display = "inline-flex";
    cluster.style.alignItems = "center";
    cluster.style.gap = "0";
    cluster.style.minWidth = "0";
    cluster.style.flexShrink = "1";
  }

  return cluster;
}

function showElement(element: Element | null | undefined) {
  if (element instanceof HTMLElement) {
    element.style.removeProperty("display");
  }
}

function ensureVolumeControl(leftGroup: Element, controlBar: Element) {
  const muteButton = controlBar.querySelector("media-mute-button");
  const volumeRange = controlBar.querySelector("media-volume-range");
  if (!muteButton || !volumeRange) return null;

  let volumeControl =
    (leftGroup.querySelector(
      "[data-wayflyer-volume-control]"
    ) as HTMLElement | null) ??
    (controlBar.querySelector(
      "[data-wayflyer-volume-control]"
    ) as HTMLElement | null);

  if (!volumeControl) {
    volumeControl = document.createElement("div");
    volumeControl.dataset["wayflyerVolumeControl"] = "";
  }

  let popup = volumeControl.querySelector(
    "[data-wayflyer-volume-popup]"
  ) as HTMLElement | null;

  if (!popup) {
    popup = document.createElement("div");
    popup.dataset["wayflyerVolumePopup"] = "";
  }

  if (volumeRange instanceof HTMLElement) {
    volumeRange.style.removeProperty("display");
  }

  popup.appendChild(volumeRange);
  volumeControl.appendChild(muteButton);
  volumeControl.appendChild(popup);
  bindVolumeHoverControl(volumeControl, popup);

  return volumeControl;
}

function bindDelayedHoverPopover(
  targets: HTMLElement[],
  show: () => void,
  hide: () => void
) {
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const cancelHide = () => {
    if (hideTimer !== null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const scheduleHide = () => {
    cancelHide();
    hideTimer = setTimeout(hide, POPOVER_HIDE_DELAY_MS);
  };

  const onEnter = () => {
    cancelHide();
    show();
  };

  for (const target of targets) {
    target.addEventListener("mouseenter", onEnter);
    target.addEventListener("mouseleave", scheduleHide);
  }

  return cancelHide;
}

function bindVolumeHoverControl(
  volumeControl: HTMLElement,
  popup: HTMLElement
) {
  if (volumeControl.dataset["wayflyerVolumeHoverBound"] === "true") {
    return;
  }

  volumeControl.dataset["wayflyerVolumeHoverBound"] = "true";

  const showPopup = () => {
    volumeControl.dataset["wayflyerPopoverOpen"] = "true";
  };

  const hidePopup = () => {
    delete volumeControl.dataset["wayflyerPopoverOpen"];
  };

  bindDelayedHoverPopover([volumeControl, popup], showPopup, hidePopup);
}

type PlaybackRateElements =
  | {
      variant: "v3";
      wrapper: null;
      button: Element;
      menu: Element;
    }
  | {
      variant: "v2";
      wrapper: Element;
      button: Element;
      menu: Element;
    };

function findPlaybackRateElements(
  controlBar: Element
): PlaybackRateElements | null {
  const v3Button = controlBar.querySelector("media-playback-rate-menu-button");
  const v3Menu = controlBar.querySelector("media-playback-rate-menu");
  if (v3Button && v3Menu) {
    return {
      variant: "v3",
      wrapper: null,
      button: v3Button,
      menu: v3Menu,
    };
  }

  const selectMenu = controlBar.querySelector("media-playback-rate-selectmenu");
  if (selectMenu) {
    const button = selectMenu.querySelector("media-playback-rate-button");
    const listbox = selectMenu.querySelector("media-playback-rate-listbox");
    if (button && listbox) {
      return {
        variant: "v2",
        wrapper: selectMenu,
        button,
        menu: listbox,
      };
    }
  }

  return null;
}

function getSelectMenuListboxSlot(selectMenu: Element) {
  return selectMenu.shadowRoot?.querySelector(
    'slot[name="listbox"]'
  ) as HTMLElement | null;
}

function syncOpenPlaybackRateMenus(controlBar: Element) {
  for (const rateControl of controlBar.querySelectorAll(
    '[data-wayflyer-playback-rate-control][data-wayflyer-popover-open="true"]'
  )) {
    if (!(rateControl instanceof HTMLElement)) continue;

    const listbox = rateControl.querySelector("media-playback-rate-listbox");
    const menu = rateControl.querySelector("media-playback-rate-menu");

    if (listbox instanceof HTMLElement) {
      positionPlaybackRateListbox(listbox, rateControl);
      continue;
    }

    if (menu instanceof HTMLElement) {
      positionPlaybackRateListbox(menu, rateControl);
    }
  }
}

function isMobileRateControl(rateControl: HTMLElement) {
  return (
    rateControl
      .closest("media-controller")
      ?.getAttribute("data-wayflyer-layout") === "mobile"
  );
}

function positionPlaybackRateListbox(
  listbox: HTMLElement,
  rateControl: HTMLElement
) {
  const mobile = isMobileRateControl(rateControl);
  listbox.style.setProperty("position", "absolute", "important");
  listbox.style.setProperty("z-index", "2", "important");

  if (mobile) {
    rateControl.style.removeProperty("--wayflyer-rate-menu-max-height");
    listbox.style.setProperty("bottom", "calc(100% + 4px)", "important");
    listbox.style.setProperty("top", "auto", "important");
    listbox.style.setProperty("left", "auto", "important");
    listbox.style.setProperty("right", "0", "important");
    listbox.style.setProperty("transform", "none", "important");
    listbox.style.removeProperty("max-height");
    listbox.style.setProperty("overflow", "visible", "important");
    listbox.style.removeProperty("overflow-y");
    listbox.style.removeProperty("overflow-x");
    listbox.style.removeProperty("scrollbar-width");
    return;
  }

  rateControl.style.removeProperty("--wayflyer-rate-menu-max-height");
  listbox.style.setProperty("bottom", "calc(100% + 8px)", "important");
  listbox.style.setProperty("top", "auto", "important");
  listbox.style.setProperty("left", "50%", "important");
  listbox.style.setProperty("right", "auto", "important");
  listbox.style.setProperty("transform", "translateX(-50%)", "important");
  listbox.style.removeProperty("max-height");
  listbox.style.removeProperty("overflow-y");
  listbox.style.removeProperty("overflow-x");
  listbox.style.removeProperty("scrollbar-width");
}

const PLAYBACK_RATE_LISTBOX_COMPACT_STYLE_ID = "wayflyer-listbox-compact";
const PLAYBACK_RATE_OPTION_COMPACT_STYLE_ID = "wayflyer-option-compact";

function ensurePlaybackRateListboxCompactStyles(listbox: HTMLElement) {
  const shadowRoot = listbox.shadowRoot;
  if (!shadowRoot) return;

  const existingStyle = shadowRoot.getElementById(
    PLAYBACK_RATE_LISTBOX_COMPACT_STYLE_ID
  );
  const compactStyles = `
    #container {
      padding: 0 !important;
      gap: 0 !important;
      overflow: visible !important;
    }

    media-chrome-option {
      box-sizing: border-box !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 32px !important;
      min-width: 32px !important;
      max-width: 32px !important;
      height: 32px !important;
      min-height: 32px !important;
      padding: 0 !important;
      margin: 0 !important;
      border: 1px solid transparent !important;
      border-radius: 200px !important;
      --media-option-focus-shadow: none;
    }

    media-chrome-option:hover,
    media-chrome-option:focus-visible {
      background: rgb(255 255 255 / 0.2) !important;
      border-color: rgb(255 255 255 / 0.2) !important;
      box-shadow: none !important;
      outline: none !important;
    }

    media-chrome-option[aria-selected="true"] {
      background: var(--pulse-700) !important;
      border-color: rgb(255 255 255 / 0.2) !important;
    }

    media-chrome-option[aria-selected="true"]:hover,
    media-chrome-option[aria-selected="true"]:focus-visible {
      background: var(--pulse-700) !important;
      border-color: rgb(255 255 255 / 0.2) !important;
      box-shadow: none !important;
      outline: none !important;
    }

    media-chrome-option > span {
      margin: 0 !important;
    }

    @media (max-width: 767px) {
      media-chrome-option {
        width: 24px !important;
        min-width: 24px !important;
        max-width: 24px !important;
        height: 24px !important;
        min-height: 24px !important;
        font-size: 10px !important;
        letter-spacing: -0.2px !important;
      }
    }
  `;

  if (existingStyle) {
    existingStyle.textContent = compactStyles;
    return;
  }

  const style = document.createElement("style");
  style.id = PLAYBACK_RATE_LISTBOX_COMPACT_STYLE_ID;
  style.textContent = compactStyles;
  shadowRoot.appendChild(style);
}

function ensurePlaybackRateOptionCompactStyles(option: HTMLElement) {
  const shadowRoot = option.shadowRoot;
  if (!shadowRoot) return;

  const existingStyle = shadowRoot.getElementById(
    PLAYBACK_RATE_OPTION_COMPACT_STYLE_ID
  );
  const compactStyles = `
    :host {
      box-sizing: border-box !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 32px !important;
      min-width: 32px !important;
      max-width: 32px !important;
      height: 32px !important;
      min-height: 32px !important;
      padding: 0 !important;
      margin: 0 !important;
      font-size: 12px !important;
      letter-spacing: -0.24px !important;
      line-height: 1.4 !important;
      border: 1px solid transparent !important;
      border-radius: 200px !important;
      --media-option-focus-shadow: none;
    }

    :host(:hover),
    :host(:focus-visible) {
      background: rgb(255 255 255 / 0.2) !important;
      border-color: rgb(255 255 255 / 0.2) !important;
      box-shadow: none !important;
      outline: none !important;
    }

    :host([aria-selected="true"]) {
      background: var(--pulse-700) !important;
      border-color: rgb(255 255 255 / 0.2) !important;
    }

    :host([aria-selected="true"]:hover),
    :host([aria-selected="true"]:focus-visible) {
      background: var(--pulse-700) !important;
      border-color: rgb(255 255 255 / 0.2) !important;
      box-shadow: none !important;
      outline: none !important;
    }

    @media (max-width: 767px) {
      :host {
        width: 24px !important;
        min-width: 24px !important;
        max-width: 24px !important;
        height: 24px !important;
        min-height: 24px !important;
        font-size: 10px !important;
        letter-spacing: -0.2px !important;
      }
    }
  `;

  if (existingStyle) {
    existingStyle.textContent = compactStyles;
    return;
  }

  const style = document.createElement("style");
  style.id = PLAYBACK_RATE_OPTION_COMPACT_STYLE_ID;
  style.textContent = compactStyles;
  shadowRoot.appendChild(style);
}

function compactPlaybackRateListbox(listbox: HTMLElement) {
  ensurePlaybackRateListboxCompactStyles(listbox);

  const options =
    listbox.shadowRoot?.querySelectorAll("media-chrome-option") ??
    listbox.querySelectorAll("media-chrome-option");

  for (const option of options) {
    if (option instanceof HTMLElement) {
      ensurePlaybackRateOptionCompactStyles(option);
    }
  }
}

function bindPlaybackRateHoverControl(
  rateControl: HTMLElement,
  elements: PlaybackRateElements
) {
  if (rateControl.dataset["wayflyerPlaybackRateHoverBound"] === "true") {
    return;
  }

  rateControl.dataset["wayflyerPlaybackRateHoverBound"] = "true";

  const isRateControlActive = () =>
    rateControl.matches(":hover") || rateControl.matches(":focus-within");

  if (elements.variant === "v3") {
    const rateMenu = elements.menu;
    const rateButton = elements.button;

    if (
      !(rateMenu instanceof HTMLElement) ||
      !(rateButton instanceof HTMLElement)
    ) {
      return;
    }

    const showMenu = () => {
      rateMenu.hidden = false;
      positionPlaybackRateListbox(rateMenu, rateControl);
      rateControl.dataset["wayflyerPopoverOpen"] = "true";
    };

    const hideMenu = () => {
      rateMenu.hidden = true;
      delete rateControl.dataset["wayflyerPopoverOpen"];
    };

    bindDelayedHoverPopover([rateControl, rateMenu], showMenu, hideMenu);

    rateControl.addEventListener("focusin", showMenu);
    rateControl.addEventListener("focusout", (event) => {
      const relatedTarget = event.relatedTarget;
      if (
        relatedTarget instanceof Node &&
        rateControl.contains(relatedTarget)
      ) {
        return;
      }
      hideMenu();
    });

    rateButton.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        showMenu();
      },
      true
    );

    rateMenu.addEventListener("change", () => {
      if (isRateControlActive()) {
        showMenu();
      }
    });

    return;
  }

  const selectMenu = elements.wrapper;
  const rateButton = elements.button;
  const listbox = elements.menu;

  if (
    !(selectMenu instanceof HTMLElement) ||
    !(rateButton instanceof HTMLElement) ||
    !(listbox instanceof HTMLElement)
  ) {
    return;
  }

  const showMenu = () => {
    const listboxSlot = getSelectMenuListboxSlot(selectMenu);
    if (listboxSlot) {
      listboxSlot.hidden = false;
    }
    compactPlaybackRateListbox(listbox);
    positionPlaybackRateListbox(listbox, rateControl);
    rateControl.dataset["wayflyerRateOpen"] = "true";
    rateControl.dataset["wayflyerPopoverOpen"] = "true";
  };

  const hideMenu = () => {
    const listboxSlot = getSelectMenuListboxSlot(selectMenu);
    if (listboxSlot) {
      listboxSlot.hidden = true;
    }
    delete rateControl.dataset["wayflyerRateOpen"];
    delete rateControl.dataset["wayflyerPopoverOpen"];
  };

  bindDelayedHoverPopover(
    [rateControl, selectMenu, listbox],
    showMenu,
    hideMenu
  );

  rateControl.addEventListener("focusin", showMenu);
  rateControl.addEventListener("focusout", (event) => {
    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && rateControl.contains(relatedTarget)) {
      return;
    }
    hideMenu();
  });

  selectMenu.addEventListener(
    "click",
    (event) => {
      if (!event.composedPath().includes(rateButton)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      showMenu();
    },
    true
  );

  listbox.addEventListener("change", () => {
    if (isRateControlActive()) {
      showMenu();
    } else {
      hideMenu();
    }
  });
}

function ensurePlaybackRateControl(rightGroup: Element, controlBar: Element) {
  const elements = findPlaybackRateElements(controlBar);
  if (!elements) return null;

  let rateControl =
    (rightGroup.querySelector(
      "[data-wayflyer-playback-rate-control]"
    ) as HTMLElement | null) ??
    (controlBar.querySelector(
      "[data-wayflyer-playback-rate-control]"
    ) as HTMLElement | null);

  if (!rateControl) {
    rateControl = document.createElement("div");
    rateControl.dataset["wayflyerPlaybackRateControl"] = "";
  }

  if (elements.variant === "v2") {
    rateControl.appendChild(elements.wrapper);
    if (elements.menu instanceof HTMLElement) {
      compactPlaybackRateListbox(elements.menu);
    }
  } else {
    rateControl.appendChild(elements.button);
    rateControl.appendChild(elements.menu);
  }

  bindPlaybackRateHoverControl(rateControl, elements);

  return rateControl;
}

function getMediaController(player: MuxPlayerElement) {
  return player.shadowRoot
    ?.querySelector("media-theme")
    ?.shadowRoot?.querySelector("media-controller");
}

function hideElement(element: Element | null | undefined) {
  if (!element) return;
  if (element instanceof HTMLElement) {
    element.style.display = "none";
  }
}

function styleFlexGroup(element: HTMLElement) {
  element.style.display = "flex";
  element.style.alignItems = "center";
  element.style.gap = "12px";
  element.style.flexShrink = "0";
  element.style.minHeight = "32px";
}

function ensureGroup(controlBar: Element, name: string) {
  let group = controlBar.querySelector(
    `[data-wayflyer-group="${name}"]`
  ) as HTMLElement | null;

  if (!group) {
    group = document.createElement("div");
    group.dataset["wayflyerGroup"] = name;
    styleFlexGroup(group);
  }

  return group;
}

function ensureRemainingTimeDisplay(
  controlBar: Element,
  currentTimeDisplay: Element
) {
  let remainingDisplay = controlBar.querySelector(
    "media-time-display[data-wayflyer-remaining]"
  );

  if (!remainingDisplay) {
    remainingDisplay = document.createElement("media-time-display");
    remainingDisplay.setAttribute("part", "bottom time display remaining");
    remainingDisplay.setAttribute("data-wayflyer-remaining", "");
    remainingDisplay.toggleAttribute(
      "disabled",
      currentTimeDisplay.hasAttribute("disabled")
    );
  }

  remainingDisplay.setAttribute("remaining", "");
  remainingDisplay.removeAttribute("showduration");

  return remainingDisplay;
}

function canShowWayflyerControls(
  player: MuxPlayerElement,
  mediaController: HTMLElement
) {
  if (mediaController.hasAttribute("mediahasplayed")) {
    return true;
  }

  const video =
    player.media?.nativeEl ?? player.shadowRoot?.querySelector("video") ?? null;

  return Boolean(video && video.readyState >= HTMLMediaElement.HAVE_METADATA);
}

function updateControlsReadyState(
  player: MuxPlayerElement,
  mediaController: HTMLElement
) {
  if (canShowWayflyerControls(player, mediaController)) {
    mediaController.dataset["wayflyerControlsReady"] = "true";
    return;
  }

  delete mediaController.dataset["wayflyerControlsReady"];
}

function styleInlineProgressBar(timeRange: HTMLElement) {
  timeRange.dataset["wayflyerInline"] = "true";
  timeRange.style.position = "static";
  timeRange.style.width = "auto";
  timeRange.style.flex = "1 1 auto";
  timeRange.style.minWidth = "0";
  timeRange.style.height = "4px";
  timeRange.style.bottom = "auto";
  timeRange.style.padding = "0";
  timeRange.style.margin = "0";
  timeRange.style.zIndex = "auto";
  timeRange.style.setProperty("--media-range-bar-color", "#fff");
  timeRange.style.setProperty("--media-range-thumb-background", "#fff");
  timeRange.style.setProperty(
    "--media-range-track-background",
    "rgb(255 255 255 / 0.1)"
  );
}

function styleControlBar(controlBar: HTMLElement, mobile: boolean) {
  controlBar.style.display = "flex";
  controlBar.style.alignItems = "center";
  controlBar.style.gap = mobile ? "0" : "16px";
  controlBar.style.width = "100%";
  controlBar.style.boxSizing = "border-box";
  if (mobile) {
    controlBar.style.justifyContent = "space-between";
    controlBar.style.padding = "0 20px 0";
  } else {
    controlBar.style.removeProperty("justify-content");
    controlBar.style.padding = "0 58px 46px";
  }
  controlBar.dataset["wayflyerLayoutApplied"] = "true";
}

function styleMobileProgressBar(timeRange: HTMLElement) {
  timeRange.dataset["wayflyerInline"] = "true";
  timeRange.style.position = "static";
  timeRange.style.width = "100%";
  timeRange.style.flex = "none";
  timeRange.style.minWidth = "0";
  timeRange.style.height = "4px";
  timeRange.style.bottom = "auto";
  timeRange.style.padding = "8px 20px 15px";
  timeRange.style.margin = "0";
  timeRange.style.zIndex = "auto";
  timeRange.style.boxSizing = "border-box";
  timeRange.style.setProperty("--media-range-bar-color", "#fff");
  timeRange.style.setProperty("--media-range-thumb-background", "#fff");
  timeRange.style.setProperty(
    "--media-range-track-background",
    "rgb(255 255 255 / 0.1)"
  );
}

function appendInOrder(
  group: Element,
  nodes: Array<Element | null | undefined>
) {
  const elements = nodes.filter(
    (node): node is Element => node instanceof Element
  );

  for (let i = 0; i < elements.length; i++) {
    const node = elements[i];
    if (!node) continue;

    const ref = group.children[i] ?? null;

    if (group.children[i] !== node) {
      group.insertBefore(node, ref);
    }
  }
}

export function applyWayflyerControlBarLayout(player: MuxPlayerElement | null) {
  if (!player) return false;

  const mediaController = getMediaController(player);
  if (!mediaController) return false;

  hideElement(mediaController.querySelector(TOP_BAR_SELECTOR));

  const controlBar = mediaController.querySelector(
    CONTROL_BAR_SELECTOR
  ) as HTMLElement | null;
  const timeRange = mediaController.querySelector(
    TIME_RANGE_SELECTOR
  ) as HTMLElement | null;
  const currentTimeDisplay = controlBar?.querySelector(CURRENT_TIME_SELECTOR);

  if (!controlBar || !timeRange || !currentTimeDisplay) return false;

  const mobile = isMobileLayout(player);

  if (mediaController instanceof HTMLElement) {
    mediaController.dataset["wayflyerLayout"] = mobile ? "mobile" : "desktop";
  }

  const themeShadowRoot = getThemeShadowRoot(player);
  if (themeShadowRoot) {
    ensureWayflyerControlStyles(themeShadowRoot);
  } else {
    return false;
  }

  for (const selector of HIDDEN_CONTROL_SELECTORS) {
    const element = controlBar.querySelector(selector);
    hideElement(element);
    if (element instanceof Element) {
      controlBar.appendChild(element);
    }
  }

  // Remove stale empty group wrappers from prior passes.
  for (const group of controlBar.querySelectorAll("[data-wayflyer-group]")) {
    if (group.childElementCount === 0) {
      group.remove();
    }
  }

  currentTimeDisplay.setAttribute("data-wayflyer-current", "");
  currentTimeDisplay.removeAttribute("remaining");
  currentTimeDisplay.removeAttribute("showduration");

  const remainingDisplay = ensureRemainingTimeDisplay(
    controlBar,
    currentTimeDisplay
  );

  if (mobile) {
    styleMobileProgressBar(timeRange);
  } else {
    styleInlineProgressBar(timeRange);
  }
  styleControlBar(controlBar, mobile);

  const leftGroup = ensureGroup(controlBar, GROUP_LEFT);
  const rightGroup = ensureGroup(controlBar, GROUP_RIGHT);
  const timeCluster = ensureTimeCluster(leftGroup);
  const timeSeparator = ensureTimeSeparator(timeCluster);

  const playButton = controlBar.querySelector(
    'media-play-button[part="bottom play button"]'
  );
  const volumeControl = ensureVolumeControl(leftGroup, controlBar);

  if (mobile) {
    appendInOrder(timeCluster, [
      currentTimeDisplay,
      timeSeparator,
      remainingDisplay,
    ]);
    appendInOrder(leftGroup, [playButton, volumeControl, timeCluster]);
  } else {
    appendInOrder(leftGroup, [playButton, volumeControl, currentTimeDisplay]);
  }

  showElement(playButton);

  if (mobile) {
    showElement(remainingDisplay);
    showElement(timeSeparator);
    showElement(timeCluster);
  } else {
    hideElement(timeSeparator);
    hideElement(timeCluster);
  }

  if (!mobile) {
    if (timeRange.parentElement !== controlBar) {
      controlBar.appendChild(timeRange);
    }
    showElement(remainingDisplay);
  } else if (timeRange.parentElement !== mediaController) {
    mediaController.appendChild(timeRange);
  }

  const playbackRateControl = ensurePlaybackRateControl(rightGroup, controlBar);
  const pipButton = controlBar.querySelector("media-pip-button");
  const fullscreenButton = controlBar.querySelector("media-fullscreen-button");

  // Figma 10780:37517 — remaining time, speed, PiP, fullscreen.
  appendInOrder(
    rightGroup,
    mobile
      ? [playbackRateControl, pipButton, fullscreenButton]
      : [remainingDisplay, playbackRateControl, pipButton, fullscreenButton]
  );

  showElement(pipButton);
  showElement(playbackRateControl);

  controlBar.appendChild(leftGroup);
  if (!mobile) {
    controlBar.appendChild(timeRange);
  }
  controlBar.appendChild(rightGroup);

  if (mobile) {
    mediaController.appendChild(controlBar);
    mediaController.appendChild(timeRange);
  }

  // Keep popovers/menus after the visible bar without affecting flex layout.
  for (const selector of [
    "media-rendition-menu",
    "media-audio-track-menu",
    "media-captions-menu",
  ]) {
    const menu = controlBar.querySelector(selector);
    if (menu instanceof Element) {
      controlBar.appendChild(menu);
    }
  }

  if (mediaController instanceof HTMLElement) {
    updateControlsReadyState(player, mediaController);
  }

  syncOpenPlaybackRateMenus(controlBar);

  return true;
}

export function observeWayflyerControlBarLayout(
  player: MuxPlayerElement,
  onLayout?: () => void
) {
  let rafId = 0;

  const scheduleLayout = () => {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      if (applyWayflyerControlBarLayout(player)) {
        onLayout?.();
      }
    });
  };

  scheduleLayout();

  const shadowRoot = player.shadowRoot;
  if (!shadowRoot) {
    return () => cancelAnimationFrame(rafId);
  }

  const observer = new MutationObserver(scheduleLayout);
  observer.observe(shadowRoot, { childList: true, subtree: true });

  const resizeObserver = new ResizeObserver(scheduleLayout);
  resizeObserver.observe(player);

  player.addEventListener("loadedmetadata", scheduleLayout);
  player.addEventListener("canplay", scheduleLayout);
  player.addEventListener("playing", scheduleLayout);

  const mediaController = getMediaController(player);
  let controllerObserver: MutationObserver | null = null;

  if (mediaController instanceof HTMLElement) {
    controllerObserver = new MutationObserver(scheduleLayout);
    controllerObserver.observe(mediaController, {
      attributes: true,
      attributeFilter: ["mediahasplayed", "mediapaused", "userinactive"],
    });
  }

  return () => {
    cancelAnimationFrame(rafId);
    observer.disconnect();
    controllerObserver?.disconnect();
    resizeObserver.disconnect();
    player.removeEventListener("loadedmetadata", scheduleLayout);
    player.removeEventListener("canplay", scheduleLayout);
    player.removeEventListener("playing", scheduleLayout);
  };
}
